import type { ComponentType } from 'react';
import {
  Activity,
  AlertTriangle,
  Banknote,
  BarChart3,
  CalendarClock,
  FileWarning,
  FolderKanban,
  ListTodo,
  MailWarning,
  PieChart,
  Sun,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PermissionAction, PermissionModule } from '../../../lib/permissionsApi';
import type { DashboardLayoutV1, LayoutWidget, WidthPreset } from '../../../lib/dashboardLayoutApi';
import {
  ActivityFeedWidget,
  IncomeExpenseWidget,
  KpiExpensesWidget,
  KpiPayablesWidget,
  KpiProfitWidget,
  KpiReceivablesWidget,
  KpiRevenueWidget,
  KpiUrgentTasksWidget,
  OverdueInvoicesWidget,
  ProjectsStatusWidget,
  RevenueDonutWidget,
  TeamAllocationWidget,
  TeamTasksWidget,
  TodayWidget,
  UpcomingPaymentsWidget,
} from './sectionWidgets';

// ---------------------------------------------------------------------------
// Configurable dashboard — widget registry. The single source of truth for
// which widgets exist, which category / permission / width presets they have
// and which component renders them. Must stay in sync with the server-side id
// list in backend/src/lib/dashboardLayout.ts.
// ---------------------------------------------------------------------------

export type { DashboardLayoutV1, LayoutWidget, WidthPreset };

export type WidgetCategory = 'financial' | 'team' | 'projects' | 'activity';

export type CanFn = (module: PermissionModule, action: PermissionAction) => boolean;

export interface WidgetDef {
  id: string;
  category: WidgetCategory;
  nameKey: string;
  descKey: string;
  icon: LucideIcon;
  component: ComponentType;
  allowedWidths: WidthPreset[];
  defaultWidth: WidthPreset;
  /** Action checked on the `dashboard` module; null = visible to everyone. */
  permission: PermissionAction | null;
}

const KPI_WIDTHS: WidthPreset[] = [2, 3, 4, 6];
const CHART_WIDTHS: WidthPreset[] = [4, 6, 8, 12];
const SECTION_WIDTHS: WidthPreset[] = [6, 8, 12];

export const WIDGET_REGISTRY: WidgetDef[] = [
  {
    id: 'kpi.revenue',
    category: 'financial',
    nameKey: 'dashboard.widget.kpi.revenue.name',
    descKey: 'dashboard.widget.kpi.revenue.desc',
    icon: TrendingUp,
    component: KpiRevenueWidget,
    allowedWidths: KPI_WIDTHS,
    defaultWidth: 2,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'kpi.expenses',
    category: 'financial',
    nameKey: 'dashboard.widget.kpi.expenses.name',
    descKey: 'dashboard.widget.kpi.expenses.desc',
    icon: TrendingDown,
    component: KpiExpensesWidget,
    allowedWidths: KPI_WIDTHS,
    defaultWidth: 2,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'kpi.profit',
    category: 'financial',
    nameKey: 'dashboard.widget.kpi.profit.name',
    descKey: 'dashboard.widget.kpi.profit.desc',
    icon: Banknote,
    component: KpiProfitWidget,
    allowedWidths: KPI_WIDTHS,
    defaultWidth: 2,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'kpi.receivables',
    category: 'financial',
    nameKey: 'dashboard.widget.kpi.receivables.name',
    descKey: 'dashboard.widget.kpi.receivables.desc',
    icon: Wallet,
    component: KpiReceivablesWidget,
    allowedWidths: KPI_WIDTHS,
    defaultWidth: 2,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'kpi.payables',
    category: 'financial',
    nameKey: 'dashboard.widget.kpi.payables.name',
    descKey: 'dashboard.widget.kpi.payables.desc',
    icon: FileWarning,
    component: KpiPayablesWidget,
    allowedWidths: KPI_WIDTHS,
    defaultWidth: 2,
    permission: 'canViewFinancialCards',
  },
  {
    // Lives in the "projects" category but keeps the current financial gating
    // (the KPI endpoint sits behind canViewFinancialCards today).
    id: 'kpi.urgentTasks',
    category: 'projects',
    nameKey: 'dashboard.widget.kpi.urgentTasks.name',
    descKey: 'dashboard.widget.kpi.urgentTasks.desc',
    icon: AlertTriangle,
    component: KpiUrgentTasksWidget,
    allowedWidths: KPI_WIDTHS,
    defaultWidth: 2,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'chart.revenueDonut',
    category: 'financial',
    nameKey: 'dashboard.widget.chart.revenueDonut.name',
    descKey: 'dashboard.widget.chart.revenueDonut.desc',
    icon: PieChart,
    component: RevenueDonutWidget,
    allowedWidths: CHART_WIDTHS,
    defaultWidth: 6,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'chart.incomeExpense',
    category: 'financial',
    nameKey: 'dashboard.widget.chart.incomeExpense.name',
    descKey: 'dashboard.widget.chart.incomeExpense.desc',
    icon: BarChart3,
    component: IncomeExpenseWidget,
    allowedWidths: CHART_WIDTHS,
    defaultWidth: 6,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'payments.upcoming',
    category: 'financial',
    nameKey: 'dashboard.widget.payments.upcoming.name',
    descKey: 'dashboard.widget.payments.upcoming.desc',
    icon: CalendarClock,
    component: UpcomingPaymentsWidget,
    allowedWidths: SECTION_WIDTHS,
    defaultWidth: 12,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'invoices.overdue',
    category: 'financial',
    nameKey: 'dashboard.widget.invoices.overdue.name',
    descKey: 'dashboard.widget.invoices.overdue.desc',
    icon: MailWarning,
    component: OverdueInvoicesWidget,
    allowedWidths: SECTION_WIDTHS,
    defaultWidth: 12,
    permission: 'canViewFinancialCards',
  },
  {
    id: 'today.panel',
    category: 'activity',
    nameKey: 'dashboard.widget.today.panel.name',
    descKey: 'dashboard.widget.today.panel.desc',
    icon: Sun,
    component: TodayWidget,
    allowedWidths: SECTION_WIDTHS,
    defaultWidth: 12,
    permission: null,
  },
  {
    id: 'team.allocation',
    category: 'team',
    nameKey: 'dashboard.widget.team.allocation.name',
    descKey: 'dashboard.widget.team.allocation.desc',
    icon: Users,
    component: TeamAllocationWidget,
    allowedWidths: SECTION_WIDTHS,
    defaultWidth: 12,
    permission: 'canViewTeamAllocation',
  },
  {
    id: 'team.tasks',
    category: 'team',
    nameKey: 'dashboard.widget.team.tasks.name',
    descKey: 'dashboard.widget.team.tasks.desc',
    icon: ListTodo,
    component: TeamTasksWidget,
    allowedWidths: SECTION_WIDTHS,
    defaultWidth: 12,
    permission: 'canViewTeamAllocation',
  },
  {
    id: 'projects.status',
    category: 'projects',
    nameKey: 'dashboard.widget.projects.status.name',
    descKey: 'dashboard.widget.projects.status.desc',
    icon: FolderKanban,
    component: ProjectsStatusWidget,
    allowedWidths: SECTION_WIDTHS,
    defaultWidth: 12,
    permission: null,
  },
  {
    id: 'activity.feed',
    category: 'activity',
    nameKey: 'dashboard.widget.activity.feed.name',
    descKey: 'dashboard.widget.activity.feed.desc',
    icon: Activity,
    component: ActivityFeedWidget,
    allowedWidths: [4, 6, 8, 12],
    defaultWidth: 12,
    permission: 'canViewActivityFeed',
  },
];

const REGISTRY_BY_ID = new Map(WIDGET_REGISTRY.map((def) => [def.id, def]));

export function getWidgetDef(id: string): WidgetDef | undefined {
  return REGISTRY_BY_ID.get(id);
}

/** Display order of catalog categories. */
export const WIDGET_CATEGORIES: WidgetCategory[] = ['financial', 'team', 'projects', 'activity'];

/** 12-column col-span presets. Literal classes so Tailwind JIT picks them up. */
// On a phone (< sm) every preset is the full row: two ~170px cards side by
// side left the KPI numbers and the task rows clipped, and the user asked for
// full-width cards there. The pairing starts at sm.
export const SPAN_CLASSES: Record<WidthPreset, string> = {
  2: 'col-span-12 sm:col-span-6 md:col-span-4 xl:col-span-2',
  3: 'col-span-12 sm:col-span-6 xl:col-span-3',
  4: 'col-span-12 md:col-span-6 xl:col-span-4',
  6: 'col-span-12 xl:col-span-6',
  8: 'col-span-12 xl:col-span-8',
  12: 'col-span-12',
};

/** Human label per width preset (fraction of the row). */
export const WIDTH_LABELS: Record<WidthPreset, string> = {
  2: '1/6',
  3: '1/4',
  4: '1/3',
  6: '1/2',
  8: '2/3',
  12: '1/1',
};

/** Reproduces today's fixed page exactly (section order of DashboardPage). */
export const DEFAULT_LAYOUT: DashboardLayoutV1 = {
  version: 1,
  widgets: [
    { id: 'kpi.revenue', w: 2 },
    { id: 'kpi.expenses', w: 2 },
    { id: 'kpi.profit', w: 2 },
    { id: 'kpi.receivables', w: 2 },
    { id: 'kpi.payables', w: 2 },
    { id: 'kpi.urgentTasks', w: 2 },
    { id: 'chart.revenueDonut', w: 6 },
    { id: 'chart.incomeExpense', w: 6 },
    { id: 'today.panel', w: 12 },
    { id: 'payments.upcoming', w: 12 },
    { id: 'invoices.overdue', w: 12 },
    { id: 'team.allocation', w: 12 },
    { id: 'team.tasks', w: 12 },
    { id: 'projects.status', w: 12 },
    { id: 'activity.feed', w: 12 },
  ],
};

/** True when the caller may see the widget (deny-by-default via `can`). */
export function isWidgetAllowed(def: WidgetDef, can: CanFn): boolean {
  return def.permission === null || can('dashboard', def.permission);
}

/** The default layout narrowed to what the caller is allowed to see. */
export function defaultLayoutFor(can: CanFn): DashboardLayoutV1 {
  return {
    version: 1,
    widgets: DEFAULT_LAYOUT.widgets.filter((w) => {
      const def = getWidgetDef(w.id);
      return def !== undefined && isWidgetAllowed(def, can);
    }),
  };
}

/**
 * Client-side sanitizer for a raw layout blob (saved server-side or default).
 * Drops unknown/duplicate ids and RBAC-forbidden widgets, clamps widths to the
 * widget's allowed presets and falls back to the default layout when the blob
 * is structurally invalid. Pure function — safe to memoize on (raw, can).
 */
export function sanitizeLayout(raw: unknown, can: CanFn): DashboardLayoutV1 {
  if (
    raw === null ||
    typeof raw !== 'object' ||
    (raw as { version?: unknown }).version !== 1 ||
    !Array.isArray((raw as { widgets?: unknown }).widgets)
  ) {
    return defaultLayoutFor(can);
  }

  const seen = new Set<string>();
  const widgets: LayoutWidget[] = [];
  for (const entry of (raw as { widgets: unknown[] }).widgets) {
    if (entry === null || typeof entry !== 'object') continue;
    const { id, w } = entry as { id?: unknown; w?: unknown };
    if (typeof id !== 'string' || seen.has(id)) continue;
    const def = getWidgetDef(id);
    if (!def || !isWidgetAllowed(def, can)) continue;
    seen.add(id);
    const width = def.allowedWidths.includes(w as WidthPreset)
      ? (w as WidthPreset)
      : def.defaultWidth;
    widgets.push({ id, w: width });
  }
  return { version: 1, widgets };
}
